import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Divider, Sheet, Stack, Table, Typography } from '@mui/joy';
import { CaretDown, CaretUp, CaretUpDown } from '@phosphor-icons/react';
import { compareForDirection, nextSort } from './tableSort';
import type { Sortable, SortDirection, SortState } from './tableSort';

function useIsMobile(breakpoint: number) {
  const mobileQuery = `(max-width: ${breakpoint - 0.05}px)`;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(mobileQuery).matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(mobileQuery);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mobileQuery]);

  return isMobile;
}

// Re-exported so callers can type their own sort state without a second import.
export type { SortDirection, SortState } from './tableSort';

export interface ResponsiveColumn<T> {
  /** Stable identifier for the column (used as React key, and as the sort key). */
  key: string;
  /** Column header. Accepts a string label or any React node (e.g. for sortable headers with click handlers). */
  header: React.ReactNode;
  /**
   * Optional plain-string label used on mobile (the "Label" half of the label/value row).
   * Falls back to `header` when it's a string. Required when `header` is a non-string node.
   */
  mobileLabel?: string;
  /** Optional desktop column width (e.g. "15%", "120px"). */
  width?: string;
  /** Skip rendering this column entirely on mobile (xs/sm). */
  hideOnMobile?: boolean;
  /**
   * Mobile rendering style:
   * - 'header' — rendered at the top of the card with no label (use for primary identifiers).
   * - 'body'   — rendered full-width below the header with no label (use for long text bodies).
   * - 'default' (the default) — rendered as a "Label: value" row in the card.
   */
  mobileVariant?: 'header' | 'body' | 'default';
  /** Cell renderer. Receives the row and returns React node. */
  render: (row: T) => React.ReactNode;
  /**
   * Makes this column's header clickable to sort.
   *
   * For a table that holds all its rows, return the value to compare and the
   * table sorts itself. For a server-paginated table, this only needs to be
   * truthy — pass `sortKey` and let the server do the ordering, because
   * sorting one page of many would silently reorder a slice and look like a
   * sort of the whole set.
   */
  sortValue?: (row: T) => Sortable;
  /**
   * Field name to send to the API (as `?ordering=`) when the table sorts
   * server-side. Defaults to `key`.
   */
  sortKey?: string;
}

export interface PaginationConfig {
  /** Rows per page. */
  pageSize: number;
  /** Plural noun for the summary line, e.g. "customers" → "Showing 1–20 of 57 customers". */
  itemNoun?: string;
  /**
   * Server-side paging: pass all three and the table renders the controls but
   * leaves the slicing to the API. Omit them and the table pages `rows` itself.
   */
  page?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
}

export interface ResponsiveTableProps<T> {
  columns: ResponsiveColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string | number;
  /**
   * Per-row action(s). Rendered as the last column on desktop (header text from `actionsHeader`),
   * and as the top-right corner of the card on mobile.
   */
  rowActions?: (row: T) => React.ReactNode;
  actionsHeader?: string;
  actionsWidth?: string;
  /** Viewport width below which dense tables switch to stacked cards. */
  cardBreakpoint?: number;
  emptyState?: React.ReactNode;
  stickyHeader?: boolean;
  /**
   * Controlled sort. Pass with `onSortChange` when the server does the
   * ordering; the table then renders the indicators and reorders nothing.
   * Leave both out and any column with `sortValue` sorts client-side.
   */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** Show a pagination footer. See `PaginationConfig`. */
  pagination?: PaginationConfig;
}

function SortIndicator({ state }: { state: SortDirection | null }) {
  if (state === 'asc') return <CaretUp size={12} weight="bold" aria-hidden />;
  if (state === 'desc') return <CaretDown size={12} weight="bold" aria-hidden />;
  return <CaretUpDown size={12} aria-hidden opacity={0.45} />;
}

export function ResponsiveTable<T>({
  columns,
  rows,
  getRowKey,
  rowActions,
  actionsHeader = 'Actions',
  actionsWidth = '10%',
  cardBreakpoint = 960,
  emptyState,
  stickyHeader = true,
  sort,
  onSortChange,
  pagination,
}: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile(cardBreakpoint);

  // Sorting is controlled when the caller handles it (server-side ordering);
  // otherwise the table keeps its own and does the work.
  const isSortControlled = onSortChange !== undefined;
  const [ownSort, setOwnSort] = useState<SortState | null>(null);
  const activeSort = isSortControlled ? sort ?? null : ownSort;

  // Likewise paging: controlled when the caller supplies page + handler.
  const isPageControlled =
    pagination?.page !== undefined && pagination?.onPageChange !== undefined;
  const [ownPage, setOwnPage] = useState(1);
  const page = isPageControlled ? (pagination?.page as number) : ownPage;

  const sortedRows = useMemo(() => {
    // A controlled sort has already been applied by whoever owns the data.
    if (isSortControlled || !activeSort) return rows;
    const col = columns.find((c) => c.key === activeSort.key);
    if (!col?.sortValue) return rows;
    const direction = activeSort.direction;
    return [...rows].sort((a, b) =>
      compareForDirection(col.sortValue!(a), col.sortValue!(b), direction)
    );
  }, [rows, columns, activeSort, isSortControlled]);

  const pagedRows = useMemo(() => {
    if (!pagination || isPageControlled) return sortedRows;
    const start = (page - 1) * pagination.pageSize;
    return sortedRows.slice(start, start + pagination.pageSize);
  }, [sortedRows, pagination, isPageControlled, page]);

  const totalCount = pagination
    ? isPageControlled
      ? pagination.totalCount ?? 0
      : sortedRows.length
    : 0;
  const totalPages = pagination ? Math.max(1, Math.ceil(totalCount / pagination.pageSize)) : 1;

  // Client-side paging only: if rows shrink under us (a filter changed), a
  // stale page number would show an empty table with no way back.
  useEffect(() => {
    if (!pagination || isPageControlled) return;
    if (page > totalPages) setOwnPage(totalPages);
  }, [pagination, isPageControlled, page, totalPages]);

  const goToPage = (next: number) => {
    if (isPageControlled) pagination?.onPageChange?.(next);
    else setOwnPage(next);
  };

  const handleSort = (col: ResponsiveColumn<T>) => {
    const next = nextSort(activeSort, col.key);
    if (isSortControlled) onSortChange?.(next);
    else setOwnSort(next);
    // Any reorder invalidates the current offset — page 3 of the old order is
    // not page 3 of the new one.
    if (pagination) goToPage(1);
  };

  const footer = pagination ? (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{
        mt: 2,
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
      }}
    >
      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
        {totalCount > 0
          ? `Showing ${Math.min((page - 1) * pagination.pageSize + 1, totalCount)}–${Math.min(
              page * pagination.pageSize,
              totalCount
            )} of ${totalCount}${pagination.itemNoun ? ` ${pagination.itemNoun}` : ''}`
          : `No ${pagination.itemNoun ?? 'results'}`}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          disabled={page <= 1}
          onClick={() => goToPage(page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => goToPage(page + 1)}
        >
          Next
        </Button>
      </Box>
    </Stack>
  ) : null;

  if (pagedRows.length === 0) {
    // Still offer the controls when an empty page is reachable — otherwise a
    // filter that empties page 3 strands the reader with no way back.
    const stranded = pagination !== undefined && page > 1;
    if (!stranded) return emptyState ? <>{emptyState}</> : null;
    return (
      <>
        {emptyState}
        {footer}
      </>
    );
  }

  if (!isMobile) {
    return (
      <>
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
          <Table stickyHeader={stickyHeader}>
            <thead>
              <tr>
                {columns.map((col) => {
                  const sortable = Boolean(col.sortValue || col.sortKey);
                  const dir = activeSort?.key === col.key ? activeSort.direction : null;
                  return (
                    <th
                      key={col.key}
                      style={col.width ? { width: col.width } : undefined}
                      aria-sort={
                        dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'
                      }
                    >
                      {sortable ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleSort(col)}
                          aria-label={`Sort by ${
                            col.mobileLabel ?? (typeof col.header === 'string' ? col.header : col.key)
                          }`}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            background: 'none',
                            border: 0,
                            padding: 0,
                            font: 'inherit',
                            color: dir ? 'primary.plainColor' : 'inherit',
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.plainColor' },
                          }}
                        >
                          {col.header}
                          <SortIndicator state={dir} />
                        </Box>
                      ) : (
                        col.header
                      )}
                    </th>
                  );
                })}
                {rowActions && (
                  <th style={actionsWidth ? { width: actionsWidth } : undefined}>{actionsHeader}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => (
                <tr key={getRowKey(row)}>
                  {columns.map((col) => (
                    <td key={col.key}>{col.render(row)}</td>
                  ))}
                  {rowActions && <td>{rowActions(row)}</td>}
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
        {footer}
      </>
    );
  }

  // Mobile: card list
  const visible = columns.filter((c) => !c.hideOnMobile);
  const headerCols = visible.filter((c) => c.mobileVariant === 'header');
  const bodyCols = visible.filter((c) => c.mobileVariant === 'body');
  const defaultCols = visible.filter(
    (c) => !c.mobileVariant || c.mobileVariant === 'default'
  );
  const sortableCols = columns.filter((c) => c.sortValue || c.sortKey);

  return (
    <>
      {sortableCols.length > 0 && (
        // There are no headers to click on a card list, so sorting gets its
        // own row of controls rather than being unavailable on mobile.
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', mr: 0.5 }}>
            Sort
          </Typography>
          {sortableCols.map((col) => {
            const dir = activeSort?.key === col.key ? activeSort.direction : null;
            return (
              <Button
                key={col.key}
                size="sm"
                variant={dir ? 'soft' : 'outlined'}
                color={dir ? 'primary' : 'neutral'}
                onClick={() => handleSort(col)}
                endDecorator={<SortIndicator state={dir} />}
                sx={{ fontWeight: 500 }}
              >
                {col.mobileLabel ?? (typeof col.header === 'string' ? col.header : col.key)}
              </Button>
            );
          })}
        </Box>
      )}

      <Stack spacing={1.5}>
        {pagedRows.map((row) => (
          <Card key={getRowKey(row)} variant="outlined" sx={{ p: 2, gap: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {(headerCols.length > 0 || rowActions) && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {headerCols.map((col) => (
                    <Box key={col.key}>{col.render(row)}</Box>
                  ))}
                </Box>
                {rowActions && <Box sx={{ flexShrink: 0 }}>{rowActions(row)}</Box>}
              </Box>
            )}

            {bodyCols.map((col) => (
              <Box key={col.key}>{col.render(row)}</Box>
            ))}

            {defaultCols.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(headerCols.length > 0 || bodyCols.length > 0) && <Divider />}
                <Stack spacing={0.75}>
                  {defaultCols.map((col) => (
                    <Box
                      key={col.key}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
                        gap: 1.5,
                        alignItems: 'start',
                      }}
                    >
                      <Typography
                        level="body-xs"
                        sx={{ color: 'text.secondary', minWidth: 0 }}
                      >
                        {col.mobileLabel ?? (typeof col.header === 'string' ? col.header : '')}
                      </Typography>
                      <Box
                        sx={{
                          textAlign: 'right',
                          minWidth: 0,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          overflowWrap: 'anywhere',
                          '& > *': { minWidth: 0, maxWidth: '100%' },
                        }}
                      >
                        {col.render(row)}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Card>
        ))}
      </Stack>
      {footer}
    </>
  );
}
